import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProjectPreview } from './components/ProjectPreviews';
import './styles/portfolio.css';

/* ─────────────────────────────────────────
   TRANSLATIONS
───────────────────────────────────────── */
const T = {
  en: {
    dir: 'ltr',
    name: 'Joud Al-lahham',
    title: 'Software Developer & Designer',
    nav: { linkedin: 'LinkedIn', github: 'GitHub', behance: 'Behance', cv: 'Download CV' },

    bio: "Hi, I'm Joud — a software developer with a heavy interest in design, from Canada. Beyond coding, I'm passionate about design, marketing, and fashion.",

    work_section: 'Work',
    career_section: 'Career',
    career_cols: ['Position and Company', 'Years', 'Responsibilities', 'Duration'],
    about_section: 'About me',
    contact_section: 'Contacts',
    contact_sub: 'Open to opportunities',
    contact_cta: "Let's talk!",
    email_label: 'Email',
    location: 'London, Ontario',

    projects: [
      {
        tag: 'Resale Platform · Mobile & Web',
        category: 'React Native, Node.js, Supabase · Product Design + Owner',
        title: 'Malabas',
        desc: 'A reselling platform for modest clothing — cross-platform mobile app and website. Built with React Native/Expo, Node.js/Express, PayPal payments, TensorFlow content moderation, and Supabase for auth and data.',
        year: '2025',
        color: '#e8ddd4',
        cta: 'View project',
        comingSoon: true,
        slug: 'malabas',
      },
      {
        tag: 'Community Super App · Mobile & Web',
        category: 'React.js, Node.js, Figma · Product Design + Owner',
        title: 'Silsila',
        desc: 'A community super app for Muslims across Canada — discover local mosques, halal restaurants, and events near you, with a built-in carpool feature. I lead product design and marketing for a fast-growing team.',
        year: '2025',
        color: '#d4dde8',
        cta: 'View project',
        comingSoon: true,
        slug: 'silsila',
      },
      {
        tag: 'Campus Navigation · Web',
        category: 'React.js, Three.js, Bootstrap · Web Developer',
        title: 'USC Campus Map',
        desc: 'Interactive 3D campus map for University of Western Ontario, improving navigation for 43,000+ students. Built with React.js and Three.js, delivered with cross-functional teams under tight deadlines.',
        year: '2023',
        color: '#d4e8da',
        cta: 'View case study',
        id: 4,
      },
      {
        tag: 'Student Productivity · Web App',
        category: 'React.js, Node.js, Figma · UI/UX Design + Developer',
        title: 'Studyscape',
        desc: 'A virtual productivity hub designed to elevate students\' at-home learning experience. Features include a customizable calendar, a Pomodoro timer, and task prioritization tools to enhance focus and organization.',
        year: '2021',
        color: '#e8e4d4',
        cta: 'View case study',
        id: 2,
      },
    ],

    career: [
      {
        role: 'Software Developer',
        company: 'Brock Solutions',
        years: 'Oct 2024 — Present',
        resp: 'Deployment & Development',
        note: 'Remote',
        duration: '',
        current: true,
      },
      {
        role: 'Web & Digital Marketing Specialist',
        company: 'ICSWO',
        years: 'Sept 2023 — Oct 2024',
        resp: 'Web & Marketing',
        note: 'London, ON',
        duration: '1y 1m',
        current: false,
      },
      {
        role: 'Web Developer Intern',
        company: 'USC — Western University',
        years: 'June 2022 — Apr 2023',
        resp: 'Web Development',
        note: 'London, ON',
        duration: '10m',
        current: false,
      },
    ],

    edu: {
      degree: 'BSc Computer Science',
      school: 'University of Western Ontario',
      year: '2024',
    },

    facts: [
      {
        label: 'Fact (1—2)',
        text: 'My first job was selling luggage and now I am working on baggage handling systems. My career trajectory feels like a lost suitcase that found its way home.',
      },
      {
        label: 'Fact (2—2)',
        text: 'I design in my free time — Figma, branding, UI. I built and designed Malabas and Silsila from scratch, end-to-end.',
      },
    ],
  },

  fr: {
    dir: 'ltr',
    name: 'Joud Al-lahham',
    title: 'Développeuse logiciel & Designer',
    nav: { linkedin: 'LinkedIn', github: 'GitHub', behance: 'Behance', cv: 'Télécharger CV' },

    bio: "Bonjour, je suis Joud — développeuse logiciel avec un fort intérêt pour le design, originaire du Canada. Au-delà du code, je suis passionnée par le design, le marketing et la mode.",

    work_section: 'Projets',
    career_section: 'Carrière',
    career_cols: ['Poste et entreprise', 'Années', 'Responsabilités', 'Durée'],
    about_section: 'À propos',
    contact_section: 'Contact',
    contact_sub: 'Ouverte aux opportunités',
    contact_cta: 'Contactez-moi !',
    email_label: 'Courriel',
    location: 'London, Ontario',

    projects: [
      {
        tag: 'Plateforme de revente · Mobile & Web',
        category: 'React Native, Node.js, Supabase · Design produit + Propriétaire',
        title: 'Malabas',
        desc: "Une plateforme de revente de vêtements modestes — application mobile multiplateforme et site web. Développée avec React Native/Expo, Node.js/Express, paiements PayPal, modération TensorFlow, et Supabase.",
        year: '2025',
        color: '#e8ddd4',
        cta: 'Voir le projet',
        comingSoon: true,
        slug: 'malabas',
      },
      {
        tag: 'Super App communautaire · Mobile & Web',
        category: 'React.js, Node.js, Figma · Design produit + Propriétaire',
        title: 'Silsila',
        desc: "Une super app communautaire pour les musulmans au Canada — mosquées, restaurants halal, événements locaux et covoiturage. Je dirige le design produit et le marketing d'une équipe en forte croissance.",
        year: '2025',
        color: '#d4dde8',
        cta: 'Voir le projet',
        comingSoon: true,
        slug: 'silsila',
      },
      {
        tag: 'Navigation campus · Web',
        category: 'React.js, Three.js, Bootstrap · Développeuse Web',
        title: 'Carte campus USC',
        desc: "Carte du campus 3D interactive pour l'Université Western Ontario, améliorant la navigation pour plus de 43 000 étudiants.",
        year: '2023',
        color: '#d4e8da',
        cta: "Voir l'étude de cas",
        id: 4,
      },
      {
        tag: 'Productivité étudiante · Web App',
        category: 'React.js, Node.js, Figma · UI/UX Design + Développeuse',
        title: 'Studyscape',
        desc: "Un hub de productivité virtuel pour améliorer l'expérience d'apprentissage à domicile des étudiants. Calendrier personnalisable, minuteur Pomodoro et outils de priorisation des tâches.",
        year: '2021',
        color: '#e8e4d4',
        cta: 'Voir l\'étude de cas',
        id: 2,
      },
    ],

    career: [
      { role: 'Développeuse logiciel', company: 'Brock Solutions', years: 'Oct 2024 — Présent', resp: 'Déploiement & Développement', note: 'Télétravail', duration: '', current: true },
      { role: 'Spécialiste Web & Marketing', company: 'ICSWO', years: 'Sept 2023 — Oct 2024', resp: 'Web & Marketing', note: 'London, ON', duration: '1a 1m', current: false },
      { role: 'Stagiaire développeuse Web', company: 'USC — Western University', years: 'Juin 2022 — Avr 2023', resp: 'Développement Web', note: 'London, ON', duration: '10m', current: false },
    ],

    edu: { degree: 'B.Sc. Informatique', school: 'Université Western Ontario', year: '2024' },

    facts: [
      { label: 'Fait (1—2)', text: "Mon premier emploi était de vendre des bagages, et maintenant je travaille sur des systèmes de traitement des bagages. Ma trajectoire ressemble à une valise perdue qui a retrouvé son chemin." },
      { label: 'Fait (2—2)', text: "Je fais du design dans mon temps libre — Figma, identité visuelle, UI. J'ai conçu Malabas et Silsila de A à Z." },
    ],
  },

  ar: {
    dir: 'rtl',
    name: 'جود اللحام',
    title: 'مطورة برمجيات ومصممة',
    nav: { linkedin: 'لينكدإن', github: 'غيثهب', behance: 'بيهانس', cv: 'تحميل السيرة الذاتية' },

    bio: "مرحباً، أنا جود — مطورة برمجيات مهتمة بالتصميم بشكل كبير، من كندا. بالإضافة إلى البرمجة، أنا شغوفة بالتصميم والتسويق والموضة.",

    work_section: 'أعمال',
    career_section: 'المسيرة المهنية',
    career_cols: ['المنصب والشركة', 'السنوات', 'المسؤوليات', 'المدة'],
    about_section: 'عني',
    contact_section: 'التواصل',
    contact_sub: 'منفتحة على الفرص',
    contact_cta: 'لنتحدث!',
    email_label: 'البريد الإلكتروني',
    location: 'لندن، أونتاريو',

    projects: [
      {
        tag: 'منصة إعادة بيع · موبايل وويب',
        category: 'React Native, Node.js, Supabase · تصميم منتج + مالكة',
        title: 'ملابس',
        desc: 'منصة لإعادة بيع الملابس المحتشمة — تطبيق موبايل متعدد المنصات وموقع ويب. مبنية بـ React Native/Expo وNode.js/Express ودفع PayPal وTensorFlow لمراقبة المحتوى وSupabase.',
        year: '٢٠٢٥',
        color: '#e8ddd4',
        cta: 'عرض المشروع',
        comingSoon: true,
        slug: 'malabas',
      },
      {
        tag: 'تطبيق مجتمعي شامل · موبايل وويب',
        category: 'React.js, Node.js, Figma · تصميم منتج + مالكة',
        title: 'سلسلة',
        desc: 'تطبيق شامل للمجتمع المسلم في كندا — اكتشف المساجد والمطاعم الحلال والفعاليات القريبة منك، مع ميزة مشاركة السيارات. أقود تصميم المنتج والتسويق لفريق سريع النمو.',
        year: '٢٠٢٥',
        color: '#d4dde8',
        cta: 'عرض المشروع',
        comingSoon: true,
        slug: 'silsila',
      },
      {
        tag: 'تنقل في الحرم الجامعي · ويب',
        category: 'React.js, Three.js, Bootstrap · مطورة ويب',
        title: 'خريطة الحرم الجامعي',
        desc: 'خريطة تفاعلية ثلاثية الأبعاد لجامعة ويسترن أونتاريو، تحسّن التنقل لأكثر من ٤٣٠٠٠ طالب.',
        year: '٢٠٢٣',
        color: '#d4e8da',
        cta: 'عرض دراسة الحالة',
        id: 4,
      },
      {
        tag: 'إنتاجية الطلاب · تطبيق ويب',
        category: 'React.js, Node.js, Figma · تصميم UI/UX + مطورة',
        title: 'Studyscape',
        desc: 'مركز إنتاجية افتراضي لتحسين تجربة التعلم المنزلي للطلاب. يتضمن تقويماً قابلاً للتخصيص، ومؤقت بومودورو، وأدوات لتحديد أولويات المهام.',
        year: '٢٠٢١',
        color: '#e8e4d4',
        cta: 'عرض دراسة الحالة',
        id: 2,
      },
    ],

    career: [
      { role: 'مطورة برمجيات', company: 'Brock Solutions', years: 'أكتوبر ٢٠٢٤ — حتى الآن', resp: 'النشر والتطوير', note: 'عن بُعد', duration: '', current: true },
      { role: 'متخصصة ويب وتسويق رقمي', company: 'ICSWO', years: 'سبتمبر ٢٠٢٣ — أكتوبر ٢٠٢٤', resp: 'ويب وتسويق', note: 'لندن، أونتاريو', duration: '١س ١ش', current: false },
      { role: 'متدربة مطورة ويب', company: 'USC — جامعة ويسترن', years: 'يونيو ٢٠٢٢ — أبريل ٢٠٢٣', resp: 'تطوير ويب', note: 'لندن، أونتاريو', duration: '١٠ أشهر', current: false },
    ],

    edu: { degree: 'بكالوريوس علوم الحاسوب', school: 'جامعة ويسترن أونتاريو', year: '٢٠٢٤' },

    facts: [
      { label: 'حقيقة (١—٢)', text: 'كانت وظيفتي الأولى بيع الحقائب، والآن أعمل على أنظمة معالجة الأمتعة. مسيرتي المهنية تشبه حقيبة ضائعة وجدت طريقها إلى المنزل.' },
      { label: 'حقيقة (٢—٢)', text: 'أصمم في وقت فراغي — Figma وهوية بصرية وواجهات مستخدم. صممت وطورت Malabas وSilsila من الألف إلى الياء.' },
    ],
  },
};

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
export default function Portfolio() {
  const navigate = useNavigate();
  const [lang, setLang] = useState('en');
  const [dark, setDark] = useState(() => localStorage.getItem('pf-dark') === 'true');
  const t = T[lang];

  /* Apply theme + direction to <html> and persist */
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
    document.documentElement.setAttribute('dir', t.dir);
    document.documentElement.setAttribute('lang', lang);
    localStorage.setItem('pf-dark', dark);
  }, [dark, t.dir, lang]);

  return (
    <div className={`pf-root${dark ? ' dark' : ''}`} dir={t.dir}>

      {/* ── FIXED NAV ── */}
      <header className="pf-nav">
        <div className="pf-nav-left">
          <div className="pf-nav-avatar" aria-hidden="true">J</div>
          <div>
            <div className="pf-nav-name">{t.name}</div>
            <div className="pf-nav-title">{t.title}</div>
          </div>
        </div>

        <div className="pf-nav-right">
          {/* Language switcher */}
          <div className="pf-lang-switch">
            {['en','ar'].map(l => (
              <button
                key={l}
                className={`pf-lang-btn${lang === l ? ' active' : ''}`}
                onClick={() => setLang(l)}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Dark/light toggle */}
          <button className="pf-theme-btn" onClick={() => setDark(d => !d)} aria-label="Toggle theme">
            {dark ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
          </button>

          <div className="pf-nav-sep" />

          <a href="https://linkedin.com/in/joudallahham" target="_blank" rel="noopener noreferrer" className="pf-nav-link">{t.nav.linkedin}</a>
          <a href="https://github.com/joudall" target="_blank" rel="noopener noreferrer" className="pf-nav-link">{t.nav.github}</a>
          <a href="/JoudAlLahham_CV.pdf" download className="pf-cv-btn">{t.nav.cv}</a>
        </div>
      </header>

      {/* ── HERO BIO ── */}
      <section className="pf-hero">
        <p className="pf-bio">{t.bio}</p>
      </section>

      {/* ── PROJECTS ── */}
      <section className="pf-section">
        {t.projects.map((proj, i) => (
          <article key={proj.title} className="pf-project">
            <div className="pf-project-meta">
              <span className="pf-project-num">(Case {i + 1}—{t.projects.length})</span>
              <span className="pf-project-tag">{proj.tag}</span>
              <span className="pf-project-year">{proj.year}</span>
            </div>

            <div className="pf-project-body">
              <div className="pf-project-left">
                <h2 className="pf-project-title">{proj.title}</h2>
                <p className="pf-project-category">{proj.category}</p>
                <p className="pf-project-desc">{proj.desc}</p>
                <button
                  className="pf-project-cta"
                  onClick={() => navigate(proj.comingSoon ? `/case-study/${proj.slug}` : `/case-study/${proj.id}`)}
                >
                  {proj.cta} <span>→</span>
                </button>
              </div>

              <div className="pf-project-image" style={{ overflow: 'hidden' }}>
                <ProjectPreview slug={proj.slug} id={proj.id} color={proj.color} />
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* ── CAREER ── */}
      <section className="pf-section">
        <div className="pf-section-meta">
          <span className="pf-section-label">{t.edu.degree} · {t.edu.school} · {t.edu.year}</span>
        </div>

        <h2 className="pf-section-title">{t.career_section}</h2>

        <div className="pf-career-header">
          {t.career_cols.map(col => (
            <span key={col}>{col}</span>
          ))}
        </div>

        <div className="pf-career-list">
          {t.career.map((job, i) => (
            <div key={i} className={`pf-career-row${job.current ? ' current' : ''}`}>
              <div className="pf-career-main">
                <div className={`pf-career-dot${job.current ? ' current' : ''}`} />
                <div>
                  <div className="pf-career-role">{job.role}</div>
                  <div className="pf-career-company">{job.company}</div>
                </div>
              </div>
              <div className="pf-career-years">{job.years}</div>
              <div className="pf-career-resp">
                <div>{job.resp}</div>
                <div className="pf-career-note">{job.note}</div>
              </div>
              <div className="pf-career-duration">{job.duration}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section className="pf-section">
        <div className="pf-section-meta">
          <span className="pf-section-label">(Not that interesting)</span>
          <span className="pf-section-label">(But two facts)</span>
        </div>

        <h2 className="pf-section-title">{t.about_section}</h2>

        <div className="pf-facts">
          {t.facts.map((f, i) => (
            <div key={i} className="pf-fact">
              <span className="pf-fact-label">{f.label}</span>
              <p className="pf-fact-text">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CONTACTS ── */}
      <section className="pf-section pf-contact-section">
        <div className="pf-section-meta">
          <span className="pf-section-label">{t.contact_sub}</span>
          <span className="pf-section-label">{t.contact_cta}</span>
        </div>

        <h2 className="pf-section-title">{t.contact_section}</h2>

        <div className="pf-contact-grid">
          <div className="pf-contact-card">
            <div className="pf-contact-name">{t.name}</div>
            <div className="pf-contact-loc">{t.location}</div>
          </div>
          <div className="pf-contact-card">
            <a href="mailto:joudallahham@gmail.com" className="pf-contact-email">joudallahham@gmail.com</a>
            <div className="pf-contact-loc">{t.email_label}</div>
          </div>
        </div>

        <div className="pf-contact-links">
          <a href="https://linkedin.com/in/joudallahham" target="_blank" rel="noopener noreferrer" className="pf-contact-link-btn">{t.nav.linkedin}</a>
          <a href="https://github.com/joudall" target="_blank" rel="noopener noreferrer" className="pf-contact-link-btn">{t.nav.github}</a>
          <a href="https://behance.net/joudlahham" target="_blank" rel="noopener noreferrer" className="pf-contact-link-btn">{t.nav.behance}</a>
          <a href="/JoudAlLahham_CV.pdf" download className="pf-contact-link-btn">{t.nav.cv}</a>
        </div>
      </section>

      <footer className="pf-footer">
        <span>2026</span>
        <span>{t.name}</span>
      </footer>

    </div>
  );
}
